import { FC, useCallback, useMemo, useState, useEffect } from 'react'
import { Segmented } from 'antd'
import { InputNew } from '@UI/InputNew'
import sclasses from '../SetCourseClassDirectionModal/index.module.css'
import spclasses from '../SetCourseClassDirectionModal/purchase.module.css'
import { useAddAlert } from '@/hooks/useAddAlert'
import classes from './discountInput.module.css'

interface DiscountInputProps {
  value?: number
  onChange?: (value: number | undefined) => void
  totalAmount?: number
  placeholder?: string | number
  className?: string
  disabled?: boolean
}

type DiscountType = 'percent' | 'amount'

const DiscountInput: FC<DiscountInputProps> = ({
  value: defValue,
  onChange,
  totalAmount = 0,
  placeholder = 0,
  className,
  disabled = false,
}) => {
  const addAlert = useAddAlert()

  const [discountType, setDiscountType] = useState<DiscountType>('amount')
  const [value, setValue] = useState<number | undefined>(defValue)

  useEffect(() => {
    if (value && totalAmount && value > totalAmount) {
      setValue(totalAmount)
      onChange?.(totalAmount)
    }
  }, [totalAmount, value, onChange, addAlert])

  // Конвертируем рубли в проценты и обратно
  const displayValue = useMemo(() => {
    if (!value || !totalAmount) return ''

    if (discountType === 'percent') {
      // Показываем в процентах
      const percentage = Math.round((value / totalAmount) * 100)
      return percentage.toString()
    } else {
      // Показываем в рублях
      return value.toString()
    }
  }, [value, totalAmount, discountType])

  const handleInputChange = useCallback(
    (inputValue: string) => {
      const numValue = parseFloat(inputValue)

      if (isNaN(numValue) || numValue < 0) {
        if (numValue < 0) {
          addAlert({
            title: 'Ошибка',
            text: 'Скидка не может быть отрицательной',
            type: 'error',
          })
        }
        setValue(undefined)
        onChange?.(undefined)
        return
      }

      let finalValue: number

      if (discountType === 'percent') {
        // Конвертируем проценты в рубли
        if (numValue > 100) {
          addAlert({
            title: 'Ошибка',
            text: 'Скидка не может превышать 100%',
            type: 'error',
          })
          return
        }
        finalValue = Math.round((numValue / 100) * totalAmount)
      } else {
        // Значение уже в рублях
        if (totalAmount && numValue > totalAmount) {
          addAlert({
            title: 'Ошибка',
            text: 'Скидка не может превышать общую стоимость',
            type: 'error',
          })
          return
        }
        finalValue = Math.round(numValue)
      }

      setValue(finalValue)
      onChange?.(finalValue)
    },
    [discountType, totalAmount, onChange, addAlert],
  )

  const handleTypeChange = useCallback((newType: number | string) => {
    setDiscountType(newType as DiscountType)
  }, [])

  const getPlaceholderText = () => {
    if (discountType === 'percent') {
      return `${placeholder}%`
    }
    return `${placeholder} руб.`
  }

  const getMaxValue = () => {
    if (discountType === 'percent') {
      return 100
    }
    return totalAmount || undefined
  }

  return (
    <div className={sclasses.inputWithLabel}>
      <div className={classes.inputTitleWrapper}>
        <div className={sclasses.inputLabel}>Скидка</div>
        <Segmented
          value={discountType}
          onChange={handleTypeChange}
          disabled={disabled}
          options={[
            { label: 'Процент', value: 'percent' },
            { label: 'Число (в руб.)', value: 'amount' },
          ]}
          className={classes.segmented}
          onPointerEnterCapture={undefined}
          onPointerLeaveCapture={undefined}
        />
      </div>
      {/* @ts-ignore */}
      <InputNew
        className={className || spclasses.input}
        type="number"
        placeholder={getPlaceholderText()}
        value={displayValue}
        onChange={(e: any) => handleInputChange(e.target.value)}
        disabled={disabled}
        min={0}
        max={getMaxValue()}
      />
    </div>
  )
}

export default DiscountInput
